// eslint-disable-next-line no-undef
let focusableElements: NodeListOf<HTMLElement> | null = null
let focusedElement: HTMLElement | null = null
let lockedElement: HTMLElement | null = null

const selectors: string[] = [
  'a[href]',
  'area[href]',
  'input:not([disabled]):not([type="hidden"]):not([aria-hidden])',
  'select:not([disabled]):not([aria-hidden])',
  'textarea:not([disabled]):not([aria-hidden])',
  'button:not([disabled]):not([aria-hidden])',
  'iframe',
  'object',
  'embed',
  '[contenteditable]',
  '[tabindex]:not([tabindex^="-"])'
]

let disabledElements: HTMLElement[] = []

/**
 * Locks the focus within a specified element and its focusable children.
 * @param { HTMLElement } element - The element to lock the focus within.
 * @param { boolean } startFocus - Optional. Determines whether to focus the first focusable element within the locked element. Defaults to `true`.
 */
function lockFocus(element: HTMLElement, startFocus: boolean = true): void {
  if (focusedElement !== null) {
    unlockFocus(false)
  }

  if (!element) {
    return
  }

  lockedElement = element
  focusedElement = document.activeElement as HTMLElement
  focusableElements = element.querySelectorAll(selectors.join(','))

  if (focusableElements.length === 0) {
    return
  }

  setTimeout(() => {
    focusedElement?.blur()

    if (startFocus && focusableElements) {
      focusableElements[0].focus()
    }

    document.addEventListener('keydown', keydownHandler)
  }, 100)
}

/**
 * Unlocks the focus and returns it to the previously focused element.
 * @param { boolean } returnFocus - Optional. Determines whether to return the focus to the previously focused element. Defaults to `true`.
 */
function unlockFocus(returnFocus: boolean = true): void {
  if (returnFocus && focusedElement) {
    focusedElement.focus()
  }

  lockedElement = null
  focusedElement = null
  focusableElements = null
  document.removeEventListener('keydown', keydownHandler)
}

/**
 * Пересобирает список фокусируемых элементов, если содержимое заблокированного
 * окна подгрузилось после открытия.
 */
function refreshFocusLock(): void {
  if (lockedElement) {
    focusableElements = lockedElement.querySelectorAll(selectors.join(','))
  }
}

function disableExternalElements(element: HTMLElement): void {
  const allFocusableElements = document.body.querySelectorAll(
    selectors.join(',')
  )
  allFocusableElements.forEach((el) => {
    if (!element.contains(el)) {
      el.setAttribute('aria-hidden', 'true')
      ;(el as HTMLElement).tabIndex = -1
      disabledElements.push(el as HTMLElement)
    }
  })
}

function enableExternalElements(): void {
  disabledElements.forEach((el) => {
    el.removeAttribute('aria-hidden')
    el.removeAttribute('tabindex')
  })
  disabledElements = []
}

function keydownHandler(event: KeyboardEvent): void {
  const isTabPressed = event.key === 'Tab'

  if (!isTabPressed) {
    return
  }

  if (!focusableElements || !lockedElement) {
    return
  }

  if (focusableElements.length === 1) {
    event.preventDefault()
    focusableElements[0].focus()
    return
  }
  if (
    focusableElements.length > 1 &&
    !lockedElement.contains(document.activeElement)
  ) {
    event.preventDefault()
    focusableElements[0].focus()
    return
  }

  if (event.shiftKey && document.activeElement === focusableElements[0]) {
    event.preventDefault()
    focusableElements[focusableElements.length - 1].focus()
    return
  }

  if (
    !event.shiftKey &&
    document.activeElement === focusableElements[focusableElements.length - 1]
  ) {
    event.preventDefault()
    focusableElements[0].focus()
  }
}

export {
  lockFocus,
  unlockFocus,
  refreshFocusLock,
  disableExternalElements,
  enableExternalElements
}
